// CustomerList.jsx
import React, { useEffect, useState, useMemo } from "react";
import api from "../api/axios";
import DocumentViewer from "../components/modal/DocumentViewer";
import { Modal } from "react-bootstrap";
import LedgerAutoForm from "../components/forms/LedgerAutoForm";

function CustomerList() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const token = localStorage.getItem("accessToken");
  const branch_id = sessionStorage.getItem("selectedContext")
    ? JSON.parse(sessionStorage.getItem("selectedContext")).branch
    : 0;

  // Modals Context Tracking States
  const [showDocs, setShowDocs] = useState(false);
  const [docClientId, setDocClientId] = useState(null);
  const [docClientName, setDocClientName] = useState("");

  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState("");

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get(`/customer-list?branch_id=${branch_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }); console.log(res.data);
        setTenants(res.data.data || []);
      } catch (err) {
        console.error("Error fetching tenants:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, [token, branch_id]);

  const filteredTenants = useMemo(() => {
    if (!searchTerm.trim()) return tenants;
    const lowerSearch = searchTerm.toLowerCase();

    return tenants.filter((tenant) => {
      const companyName = tenant.company_name ? tenant.company_name.toLowerCase() : "";
      const officeNo = tenant.office_data?.office_no ? String(tenant.office_data.office_no).toLowerCase() : "";
      return companyName.includes(lowerSearch) || officeNo.includes(lowerSearch);
    });
  }, [tenants, searchTerm]);

  const pureMonthsList = useMemo(() => {
    const monthsSet = new Set();
    tenants.forEach((t) => {
      if (t.monthly_rents) {
        Object.keys(t.monthly_rents).forEach((mKey) => monthsSet.add(mKey));
      }
    });
    return Array.from(monthsSet).sort();
  }, [tenants]);

  const columnsStructure = useMemo(() => {
    const yearsSet = new Set();
    pureMonthsList.forEach((mKey) => yearsSet.add(mKey.split("-")[0]));

    const columns = [];
    const sortedYears = Array.from(yearsSet).sort();

    sortedYears.forEach((year) => {
      columns.push({ type: "year_total", yearKey: year, label: `${year} Total` });
      pureMonthsList.forEach((mKey) => {
        if (mKey.startsWith(`${year}-`)) {
          const [_, month] = mKey.split("-");
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          const monthLabel = date.toLocaleString("default", { month: "short", year: "2-digit" });
          columns.push({ type: "month", monthKey: mKey, label: monthLabel });
        }
      });
    });

    return columns;
  }, [pureMonthsList]);

  const footerColumnTotals = useMemo(() => {
    const totals = {};
    columnsStructure.forEach((col) => {
      let currentSum = 0;
      filteredTenants.forEach((tenant) => {
        if (col.type === "month") {
          currentSum += tenant.monthly_rents?.[col.monthKey] || 0;
        } else if (col.type === "year_total") {
          currentSum += tenant.yearly_totals?.[col.yearKey] || 0;
        }
      });
      totals[col.type === "month" ? col.monthKey : `year_${col.yearKey}`] = currentSum;
    });
    return totals;
  }, [filteredTenants, columnsStructure]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handleExportToExcel = () => {
    let tableHtml = `
      <table border="1">
        <thead>
          <tr style="background-color: #f2f2f2; font-weight: bold;">
            <th>Office</th>
            <th>Tenant</th>
            <th>Contract Period</th>
            <th>Reg.</th>
            ${columnsStructure.map((col) => `<th>${col.label}</th>`).join("")}
          </tr>
        </thead>
        <tbody>
          ${filteredTenants.map((tenant) => {
      const onboard = tenant.tenant_onboard_linking?.[0]?.onboard;
      const office = tenant.office_data?.office_no || "-";
      const start = onboard?.contarct_start_date ? formatDate(onboard.contarct_start_date) : "-";
      const end = onboard?.contarct_end_date ? formatDate(onboard.contarct_end_date) : "-";
      const reg = tenant.pro_register_status == 1 ? "Yes" : "No";

      return `
              <tr>
                <td>${office}</td>
                <td>${tenant.company_name}</td>
                <td>${start} - ${end}</td>
                <td>${reg}</td>
                ${columnsStructure
          .map((col) => {
            const val = col.type === "month" ? tenant.monthly_rents?.[col.monthKey] : tenant.yearly_totals?.[col.yearKey];
            return `<td>${val !== undefined ? val.toFixed(2) : "-"}</td>`;
          })
          .join("")}
              </tr>
            `;
    }).join("")}
        </tbody>
        <tfoot>
          <tr style="font-weight: bold; background-color: #e6e6e6;">
            <td colspan="4" align="right">Total Rent:</td>
            ${columnsStructure
        .map((col) => {
          const totalVal = col.type === "month" ? footerColumnTotals[col.monthKey] : footerColumnTotals[`year_${col.yearKey}`];
          return `<td>${totalVal ? totalVal.toFixed(2) : "0.00"}</td>`;
        })
        .join("")}
          </tr>
        </tfoot>
      </table>
    `;

    const template = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head></head><body>${tableHtml}</body>
      </html>
    `;

    const blob = new Blob([template], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Filtered_Tenant_Rent_Report_${new Date().getFullYear()}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ENHANCED EXPORT PAYMENT INFO MATRIX WITH PARENT PROPERTY EXTRACTIONS
  const handleExportPaymentInfo = () => {
    let maxSecurityChequesCount = 0;
    let maxDirectPaymentsCount = 0;
    let maxChequesPerMonthCounter = {};

    pureMonthsList.forEach((mKey) => {
      maxChequesPerMonthCounter[mKey] = 0;
    });

    filteredTenants.forEach((tenant) => {
      const sCount = tenant.security_cheques?.length || 0;
      if (sCount > maxSecurityChequesCount) maxSecurityChequesCount = sCount;

      const dCount = tenant.direct_payments?.length || 0;
      if (dCount > maxDirectPaymentsCount) maxDirectPaymentsCount = dCount;

      pureMonthsList.forEach((mKey) => {
        const mCount = tenant.cheques_by_month?.[mKey]?.length || 0;
        if (mCount > maxChequesPerMonthCounter[mKey]) {
          maxChequesPerMonthCounter[mKey] = mCount;
        }
      });
    });

    if (maxSecurityChequesCount === 0) maxSecurityChequesCount = 1;
    if (maxDirectPaymentsCount === 0) maxDirectPaymentsCount = 1;
    pureMonthsList.forEach((mKey) => {
      if (maxChequesPerMonthCounter[mKey] === 0) maxChequesPerMonthCounter[mKey] = 1;
    });

    let row1Headers = `
      <tr style="background-color: #1b4d3e; color: #ffffff; font-weight: bold;">
        <th rowspan="2" valign="middle">Office</th>
        <th rowspan="2" valign="middle">Tenant</th>
        <th colspan="${maxDirectPaymentsCount * 5}" align="center" style="background-color: #2b579a;">Bank / Card / POS</th>
        ${pureMonthsList.map((mKey) => {
      const [yr, mn] = mKey.split("-");
      const lbl = new Date(parseInt(yr), parseInt(mn) - 1, 1).toLocaleString("default", { month: "short", year: "2-digit" });
      return `<th colspan="${maxChequesPerMonthCounter[mKey] * 4}" align="center">${lbl.toUpperCase()}</th>`;
    }).join("")}
        <th colspan="${maxSecurityChequesCount * 3}" align="center" style="background-color: #8b0000;">SECURITY CHEQUE DETAILS</th>
      </tr>
    `;

    let row2Headers = `
      <tr style="background-color: #2e7d32; color: #ffffff; font-weight: bold;">
        ${Array.from({ length: maxDirectPaymentsCount }).map(() => `<th>Bank</th><th>Account Name</th><th>Reference No</th><th>Date</th><th>Amount</th>`).join("")}
        ${pureMonthsList.map((mKey) => {
      let innerCols = "";
      for (let i = 0; i < maxChequesPerMonthCounter[mKey]; i++) {
        innerCols += `<th>Bank</th><th>Cheque No</th><th>Date</th><th>Amount</th>`;
      }
      return innerCols;
    }).join("")}
        ${Array.from({ length: maxSecurityChequesCount }).map(() => `<th>Bank</th><th>Cheque No</th><th>Amount</th>`).join("")}
      </tr>
    `;

    let bodyRows = "";
    filteredTenants.forEach((tenant) => {
      let tenantRowHeight = 1;

      pureMonthsList.forEach((mKey) => {
        const len = tenant.cheques_by_month?.[mKey]?.length || 0;
        if (len > tenantRowHeight) tenantRowHeight = len;
      });
      if ((tenant.security_cheques?.length || 0) > tenantRowHeight) tenantRowHeight = tenant.security_cheques.length;
      if ((tenant.direct_payments?.length || 0) > tenantRowHeight) tenantRowHeight = tenant.direct_payments.length;

      const officeNo = tenant.office_data?.office_no || "-";
      const companyName = tenant.company_name || "-";

      for (let rIdx = 0; rIdx < tenantRowHeight; rIdx++) {
        let rowHtml = "<tr>";

        if (rIdx === 0) {
          rowHtml += `
            <td rowspan="${tenantRowHeight}" valign="middle">${officeNo}</td>
            <td rowspan="${tenantRowHeight}" valign="middle"><strong>${companyName}</strong></td>
          `;
        }

        // Direct non-cheque payments rendering layout block
        const dirArr = tenant.direct_payments || [];
        if (rIdx < dirArr.length) {
          const dItem = dirArr[rIdx];
          rowHtml += `<td>${dItem.bank}</td><td>${dItem.account_name}</td><td>${dItem.reference_no}</td><td>${dItem.date}</td><td>${dItem.amount.toFixed(2)}</td>`;
          for (let p = 1; p < maxDirectPaymentsCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td><td>-</td>`;
          }
        } else if (rIdx === 0 && dirArr.length === 0) {
          for (let p = 0; p < maxDirectPaymentsCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td><td>-</td>`;
          }
        } else {
          for (let p = 0; p < maxDirectPaymentsCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td><td>-</td>`;
          }
        }

        // Dated month-wise tracking lists loop
        pureMonthsList.forEach((mKey) => {
          const maxChequeSlots = maxChequesPerMonthCounter[mKey];
          const chequesArr = tenant.cheques_by_month?.[mKey] || [];

          if (rIdx < chequesArr.length) {
            const chk = chequesArr[rIdx];
            rowHtml += `<td>${chk.bank}</td><td>${chk.cheque_no}</td><td>${chk.date}</td><td>${chk.amount.toFixed(2)}</td>`;
            for (let p = 1; p < maxChequeSlots; p++) {
              rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td>`;
            }
          } else if (rIdx === 0 && chequesArr.length === 0) {
            for (let p = 0; p < maxChequeSlots; p++) {
              rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td>`;
            }
          } else {
            for (let p = 0; p < maxChequeSlots; p++) {
              rowHtml += `<td>-</td><td>-</td><td>-</td><td>-</td>`;
            }
          }
        });

        // Un-dated/Security instrument blocks rendering execution
        const secArr = tenant.security_cheques || [];
        if (rIdx < secArr.length) {
          const sChk = secArr[rIdx];
          rowHtml += `<td>${sChk.bank}</td><td>${sChk.cheque_no}</td><td>${sChk.amount.toFixed(2)}</td>`;
          for (let p = 1; p < maxSecurityChequesCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td>`;
          }
        } else if (rIdx === 0 && secArr.length === 0) {
          for (let p = 0; p < maxSecurityChequesCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td>`;
          }
        } else {
          for (let p = 0; p < maxSecurityChequesCount; p++) {
            rowHtml += `<td>-</td><td>-</td><td>-</td>`;
          }
        }

        rowHtml += "</tr>";
        bodyRows += rowHtml;
      }
    });

    const fullTemplate = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Payment Report</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>
        <body>
          <table border="1">
            <thead>${row1Headers}${row2Headers}</thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([fullTemplate], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Tenant_Categorized_Payment_Report_${new Date().getFullYear()}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDocuments = (clientId, clientName) => {
    setDocClientId(clientId);
    setDocClientName(clientName);
    setShowDocs(true);
  };

  const openLedgerModal = (companyName, userId) => {
    setSelectedCompanyName(companyName);
    setSelectedMasterId(userId);
    setShowLedgerModal(true);
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Customers/Tenants</h4>
            <div className="page-title-right d-flex align-items-center">
              <button
                className="btn btn-success btn-sm me-2"
                onClick={handleExportToExcel}
                disabled={filteredTenants.length === 0}
              >
                <i className="ri-file-excel-2-line align-bottom me-1"></i> Export Tenant List
              </button>

              <button
                className="btn btn-secondary btn-sm"
                onClick={handleExportPaymentInfo}
                disabled={filteredTenants.length === 0}
                style={{ backgroundColor: "#1b4d3e", borderColor: "#1b4d3e" }}
              >
                <i className="ri-secure-payment-line align-bottom me-1"></i> Export Payment Info
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4 col-sm-6">
          <div className="input-group">
            <span className="input-group-text bg-light"><i className="ri-search-line"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by Tenant name or Office no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>Clear</button>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Customer / Tenant List</h4>
        </div>
        <div className="card-body">
          {loading ? (
            <p>Loading Workspace Matrix Data...</p>
          ) : (
            <div className="table-responsive ledger-scroll">
              <table className="table table-striped table-bordered align-middle text-nowrap">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Office</th>
                    <th>Tenant</th>
                    <th>Contract Period</th>
                    <th title="PRO Registration Done">Reg.</th>
                    {columnsStructure.map((col, index) => (
                      <th
                        key={index}
                        className="text-center text-uppercase"
                        style={col.type === "year_total" ? { backgroundColor: "#eaedf1", fontWeight: "bold", minWidth: "110px" } : {}}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.length > 0 ? (
                    filteredTenants.map((tenant) => {
                      const onboard = tenant.tenant_onboard_linking?.[0]?.onboard;
                      const office = tenant.office_data?.office_no || "-";
                      const startDate = onboard?.contarct_start_date ? new Date(onboard.contarct_start_date) : null;
                      const endDate = onboard?.contarct_end_date ? new Date(onboard.contarct_end_date) : null;

                      return (
                        <tr key={tenant.id}>
                          <td>{office}</td>
                          <td><strong>{tenant.company_name}</strong></td>
                          <td>{startDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : "-"}</td>
                          <td className="text-center">
                            {tenant.pro_register_status == 1 ? (
                              <span className="badge bg-success">✓</span>
                            ) : (
                              <span className="badge bg-danger">✗</span>
                            )}
                          </td>

                          {columnsStructure.map((col, cIdx) => {
                            const isYear = col.type === "year_total";
                            const value = isYear ? tenant.yearly_totals?.[col.yearKey] : tenant.monthly_rents?.[col.monthKey];
                            return (
                              <td key={cIdx} className="text-center" style={isYear ? { backgroundColor: "#f8f9fa", fontWeight: "bold" } : {}}>
                                {value !== undefined ? (
                                  <span className={isYear ? "text-primary" : "text-dark"}>
                                    {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                ) : (
                                  <span className="text-muted text-opacity-25">-</span>
                                )}
                              </td>
                            );
                          })}

                          <td>
                            <div className="d-flex">
                              <button className="btn btn-primary btn-sm me-2" onClick={() => handleViewDocuments(onboard?.id, onboard?.company_name)}>Files</button>
                              {tenant.has_ledger === false && (
                                <button className="btn btn-info btn-sm" onClick={() => openLedgerModal(tenant.company_name, tenant.user_id)}>Generate Ledger</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr><td colSpan={5 + columnsStructure.length} className="text-center">No records match your filters.</td></tr>
                  )}
                </tbody>
                {filteredTenants.length > 0 && (
                  <tfoot className="table-light fw-bold" style={{ borderTop: "2px solid #ccc" }}>
                    <tr>
                      <td colSpan="4" className="text-end text-uppercase">Total:</td>
                      {columnsStructure.map((col, fIdx) => {
                        const targetKey = col.type === "month" ? col.monthKey : `year_${col.yearKey}`;
                        const sumValue = footerColumnTotals[targetKey] || 0;
                        return (
                          <td key={fIdx} className="text-center" style={col.type === "year_total" ? { backgroundColor: "#eaedf1" } : {}}>
                            {sumValue > 0 ? sumValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00"}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      <DocumentViewer clientId={docClientId} clientName={docClientName} show={showDocs} onClose={() => setShowDocs(false)} />
      <Modal show={showLedgerModal} onHide={() => setShowLedgerModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>Create Ledger</Modal.Title></Modal.Header>
        <Modal.Body>
          <LedgerAutoForm prefillName={selectedCompanyName} prefillMasterId={selectedMasterId} onSuccess={() => setShowLedgerModal(false)} />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CustomerList;